import type { ReactNode } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

type EditorSize = 'standard' | 'wide' | 'workspace'

const dialogSizes: Record<EditorSize, string> = {
  standard: 'sm:max-w-3xl',
  wide: 'sm:max-w-5xl',
  workspace: 'h-[min(860px,calc(100dvh-1.5rem))] sm:max-w-6xl',
}

interface EditorChromeProps {
  title: ReactNode
  description?: ReactNode
  icon?: ReactNode
  closeLabel: string
}

interface EditorDialogProps extends EditorChromeProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
  footer?: ReactNode
  size?: EditorSize
  bodyClassName?: string
  contentClassName?: string
  testId?: string
}

export function FdeEditorDialog({
  open,
  onOpenChange,
  title,
  description,
  icon,
  closeLabel,
  children,
  footer,
  size = 'wide',
  bodyClassName,
  contentClassName,
  testId,
}: EditorDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={testId}
        className={cn(
          'flex max-h-[calc(100dvh-1.5rem)] w-[calc(100vw-1rem)] flex-col gap-0 overflow-hidden border-border bg-card p-0 shadow-2xl sm:rounded-2xl [&>button]:hidden',
          dialogSizes[size],
          contentClassName,
        )}
      >
        <DialogHeader className="relative shrink-0 space-y-1 border-b border-border px-5 py-4 pr-14 text-left sm:px-6">
          <DialogTitle className="flex items-center gap-2.5 text-base leading-6">
            {icon && <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary [&_svg]:size-4">{icon}</span>}
            <span className="min-w-0 truncate">{title}</span>
          </DialogTitle>
          {description && <DialogDescription className={cn('text-xs leading-5', icon && 'sm:pl-10')}>{description}</DialogDescription>}
          <DialogClose asChild>
            <Button type="button" size="icon-sm" variant="ghost" className="absolute right-4 top-4" aria-label={closeLabel}>
              <X />
            </Button>
          </DialogClose>
        </DialogHeader>
        <div className={cn('min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain px-5 py-5 sm:px-6', bodyClassName)}>
          {children}
        </div>
        {footer && <footer className="flex shrink-0 flex-col-reverse gap-2 border-t border-border bg-muted/10 px-5 py-3.5 sm:flex-row sm:items-center sm:justify-end sm:px-6">{footer}</footer>}
      </DialogContent>
    </Dialog>
  )
}

interface EditorPanelProps extends EditorChromeProps {
  children: ReactNode
  footer?: ReactNode
  onClose: () => void
  bodyClassName?: string
  testId?: string
}

export function FdeEditorPanel({ title, description, icon, closeLabel, children, footer, onClose, bodyClassName, testId }: EditorPanelProps) {
  return (
    <aside
      data-testid={testId}
      className="absolute inset-y-0 right-0 z-30 flex w-full max-w-[42rem] animate-in flex-col overflow-hidden border-l border-border bg-card shadow-[-20px_0_50px_-32px_rgba(15,23,42,0.55)] slide-in-from-right-4 duration-200"
    >
      <header className="relative shrink-0 border-b border-border px-5 py-4 pr-14">
        <h3 className="flex items-center gap-2.5 text-sm font-semibold leading-5 text-foreground">
          {icon && <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary [&_svg]:size-4">{icon}</span>}
          <span className="min-w-0 truncate">{title}</span>
        </h3>
        {description && <p className={cn('mt-1 text-xs leading-5 text-muted-foreground', icon && 'sm:pl-10')}>{description}</p>}
        <Button type="button" size="icon-sm" variant="ghost" className="absolute right-4 top-3.5" aria-label={closeLabel} onClick={onClose}>
          <X />
        </Button>
      </header>
      <div className={cn('min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain px-5 py-5 sm:px-6', bodyClassName)}>{children}</div>
      {footer && <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-border bg-muted/10 px-5 py-3.5 sm:px-6">{footer}</footer>}
    </aside>
  )
}
